import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useAppointableDisable,
  useAppointableEdit,
  useAppointableEnable,
  useAppointableList,
  useMutationDeleteAppointable,
} from "@gymflow/api";
import { formatCurrency } from "@gymflow/helpers";
import { AppointableDTO, AppointableDtoStatus } from "@gymflow/types";
import { createColumnHelper, SortingState } from "@tanstack/react-table";
import { copyToClipboard } from "apps/portal/src/helpers";
import { useContext, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { usePageSize } from "../../../hooks/usePageSize";
import { usePortalRoutes } from "../../../hooks/usePortalRoutes";
import { ModalContext, useClubSettings } from "../../../providers";
import { RouteFeature } from "../../../routes/feature";
import { RouteLayout } from "../../../routes/layout";
import useGymflowModels from "../../../store";
import { Badge, Button, CopyIcon, ReactiveButton, Switch } from "../../atoms";
import DropdownFilter from "../../DropdownFilter";
import { PaginatedTable } from "../../organisms/PaginatedTable";
import { ConfirmModal } from "../../templates";
import { SettingsContainer } from "../SettingsContainer";

export function AppointableTable() {
  const { t } = useTranslation();
  const { createAbsoluteClubLink, createClubLink } = usePortalRoutes();
  const { api } = useGymflowModels();
  const { club } = useClubSettings();
  const { mutateAsync: enable } = useAppointableEnable({ api });
  const { mutateAsync: mutateAsyncDeleteAppointable } =
    useMutationDeleteAppointable({ api });
  const { mutateAsync: disable } = useAppointableDisable({ api });
  const { mutateAsync: changeAppointable } = useAppointableEdit({ api });
  const { setModal, hide } = useContext(ModalContext);
  const [isActiveOption, setIsActiveOption] = useState<{
    label: string;
    value: AppointableDtoStatus;
  }>({
    label: "Enabled",
    value: "ACTIVE",
  });
  const history = useHistory();

  const [selectedPage, setSelectedPage] = useState(0);
  const [sort, setSort] = useState<SortingState>([]);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const pageSize = usePageSize({
    tableContainerRef,
    rowHeight: 56,
  });

  const { data, isLoading } = useAppointableList({
    api,
    opts: {
      page: selectedPage,
      limit: pageSize,
      sort: sort?.[0]
        ? {
            field: sort[0].id,
            desc: sort[0].desc,
          }
        : undefined,
      extraParams: { status: isActiveOption.value as AppointableDtoStatus },
    },
  });

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<AppointableDTO>();
    return [
      columnHelper.accessor("name", {}),
      columnHelper.accessor("price", {
        cell: (column) => {
          return (
            <div className="">
              {column.row.original.pricingModel === "FREE" && <>Free</>}
              {column.row.original.pricingModel === "FIXED" &&
                column.row.original.price &&
                formatCurrency(column.row.original.price, club.defaultCurrency)}
            </div>
          );
        },
      }),
      columnHelper.accessor("duration", {
        cell: (column) => {
          return `${column.row.original.duration} minutes`;
        },
      }),
      columnHelper.accessor("appointableCategory.name", {
        header: "Category",
        cell: (column) => {
          return <Badge>{column.row.original.appointableCategory.name}</Badge>;
        },
      }),
      columnHelper.accessor("allowOnlineBookings", {
        header: "On Web",
        cell: (cell) => {
          return (
            <div className="flex items-center justify-start">
              <Switch
                value={cell.getValue()}
                onChange={async (checked) => {
                  await changeAppointable({
                    appointableId: cell.row.original.id,
                    patchedFields: { allowOnlineBookings: checked },
                  });
                }}
              />
            </div>
          );
        },
      }),

      columnHelper.accessor("allowAppBookings", {
        header: "On App",
        cell: (cell) => {
          return (
            <div className="flex items-center justify-start">
              <Switch
                value={cell.getValue()}
                onChange={async (checked) => {
                  await changeAppointable({
                    appointableId: cell.row.original.id,
                    patchedFields: { allowAppBookings: checked },
                  });
                }}
              />
            </div>
          );
        },
      }),
      columnHelper.accessor("id", {
        header: "Appointment Link",
        cell: (column) => (
          <ReactiveButton
            size="small"
            onClick={async () => {
              await copyToClipboard(
                createAbsoluteClubLink(
                  RouteLayout.Site,
                  RouteFeature.AppointableId.replace(
                    ":appointableId",
                    column.getValue().toString(),
                  ),
                ),
              );
            }}
            reactiveContent="Link Copied"
            className="mr-4 w-32"
          >
            <div className="flex w-full items-center gap-1">
              <CopyIcon pathClassName="stroke-gray-500" />
              Copy Link
            </div>
          </ReactiveButton>
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (props) => {
          const isEnabled = props.row.original.status === "ACTIVE";
          const id = props.row.original.id;
          const renderBody = () => {
            let text_part1 = "";

            if (isEnabled) {
              text_part1 = "Are you sure you want to disable this appointment?";
            } else {
              text_part1 = "Are you sure you want to enable this appointment?";
            }
            return (
              <div className="mt-2 text-sm text-gray-600">{text_part1}</div>
            );
          };

          return (
            <div className="flex gap-1.5">
              <Button
                intent="link"
                onClick={() => {
                  history.push(
                    createClubLink(
                      RouteLayout.Staff,
                      RouteFeature.AppointableWizardEdit.replace(
                        ":appointableId",
                        props.row.original.id.toString(),
                      ),
                    ),
                  );
                }}
              >
                Edit
              </Button>
              <Button
                intent="link-warning"
                onClick={() => {
                  setModal(
                    <ConfirmModal
                      type={isEnabled ? "danger" : "secondary"}
                      title={
                        isEnabled ? "Disable Appointment" : "Enable Appointment"
                      }
                      onConfirm={async () => {
                        if (isEnabled) {
                          await disable(id);
                        } else {
                          await enable(id);
                        }
                        hide();
                      }}
                      onCancel={() => {
                        hide();
                      }}
                    >
                      {renderBody()}
                    </ConfirmModal>,
                  );
                }}
              >
                {isEnabled ? "Disable" : "Enable"}
              </Button>
              <Button
                intent="link-error"
                onClick={() => {
                  setModal(
                    <ConfirmModal
                      title={t(
                        "page.settings.tab.appointable.table.actions.delete.confirmModal.title",
                      )}
                      type="danger"
                      onConfirm={async () => {
                        await mutateAsyncDeleteAppointable(id);

                        hide();
                      }}
                      onCancel={() => {
                        hide();
                      }}
                    >
                      {t(
                        "page.settings.tab.appointable.table.actions.delete.confirmModal.description",
                      )}
                    </ConfirmModal>,
                  );
                }}
              >
                {t("portal.common.delete")}
              </Button>
            </div>
          );
        },
      }),
    ];
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    changeAppointable,
    createAbsoluteClubLink,
    createClubLink,
    disable,
    enable,
    hide,
    history,
    setModal,
    club.defaultCurrency,
  ]);

  return (
    <SettingsContainer
      title="Appointments"
      subTitle="Create, Update and manage your club's appointments."
      actions={
        <>
          <DropdownFilter
            className="mt-0"
            options={[
              {
                label: "Enabled",
                value: "ACTIVE",
              },
              {
                label: "Disabled",
                value: "INACTIVE",
              },
            ]}
            value={isActiveOption}
            onChange={(v: any) => setIsActiveOption(v)}
          />
          <Button
            intent="secondary"
            onClick={() => {
              history.push(
                createClubLink(
                  RouteLayout.Staff,
                  RouteFeature.AppointableWizardCreate,
                ),
              );
            }}
          >
            <div className="flex flex-row items-center gap-x-2">
              <FontAwesomeIcon icon={faPlus} />
              <div>Appointment</div>
            </div>
          </Button>
        </>
      }
    >
      <PaginatedTable
        tableProps={{
          data: data?.content ?? [],
          columns: columns,
          pageCount: data?.totalPages ?? 0,
          pageIndex: data?.number ?? 0,
          onSortingChange: setSort,
          sort: sort,
          isFetching: isLoading,
          pageSize: pageSize,
          tableContainerRef,
        }}
        hasPreviousPage={( data?.number ??0) > 0}
        hasNextPage={( data?.number ??0) < (data?.totalPages ?? 0) - 1}
        goToNextPage={() => {
          setSelectedPage((e) => e + 1);
        }}
        goToPreviousPage={() => {
          setSelectedPage((e) => e - 1);
        }}
      />
    </SettingsContainer>
  );
}
